import { useEffect, useState } from "react";
import { Card, Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, CancelButton, MoreButton } from "ui-components/IconButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import { useCustomerValue } from "providers/CustomerContext";
import {
  deleteCOReceiving,
  getRecvStatusCount,
  getRecvQueueStatusCount,
  createPalletize,
} from "services/data.inbound.service";

import { statusObj } from "layouts/views/common/enum";
import CORecvLog from "./components/CORecvLog";
import BasicQueueTabs from "ui-components/BasicQueueTabs";
import { getReceivingList } from "services/data.inbound.service";
import { TableCard } from "ui-components/Layout";
import { useTeamValue } from "providers/TeamContext";
import { LoadingDialog } from "ui-components/Alert";
import MDBadge from "components/MDBadge";
import RecvQtyLog from "../receiving/components/RecvQtyLog";

function CORecvList() {
  const {permissions, allowAll} = useTeamValue();
  const { customerCode: custCode } = useCustomerValue();
  const enabled = Boolean(custCode);
  const navigate = useNavigate();
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Open");
  const type = "SLRTRECV";
  const [queueStatus, setQueueStatus] = useState("");
  const [queueTabs, setQueueTabs] = useState([]);
  const [click, setClick] = useState(false)
  const [tabs, setTabs] = useState([
    { label: "Open", count: 0 },
    { label: "Cancelled", count: 0 },
    { label: "Received", count: 0 },
  ]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);

  const addPermission = allowAll || (Boolean(permissions["CO Receiving"]) && permissions["CO Receiving"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["CO Receiving"]) && permissions["CO Receiving"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["CO Receiving"]) && permissions["CO Receiving"].findIndex(b => b==="deactivate") !== -1);

  useEffect(() => {
    if (location.state) {
      setAlert(<Success content={location.state} onChange={setAlert} />);
    }
  }, []);
  useQuery({
    queryKey: ["co-recv-list", { custCode, status, queueStatus }],
    queryFn: () => getReceivingList({ custCode, status: statusObj[status], type, queueStatus }),
    enabled,
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["co-recv-list", "status", { custCode,status, queueStatus }],
    queryFn: () => getRecvStatusCount({ custCode, type }),
    enabled,
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["co-recv-list", "queue-status", { custCode }],
    queryFn: () => getRecvQueueStatusCount({ custCode, type }),
    enabled: Boolean(custCode) && status === "Open",
    onSuccess: (data) => setQueueTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deleteCOReceiving, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["co-recv-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });

  const palletizeMutation = useMutation(createPalletize, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["co-recv-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });

  const palletizeStatus = palletizeMutation.status === "loading";

  const onEdit = (req) => navigate("/inbound/co-receiving/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Cancel CO Receiving Code ${req.recvCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const [logRequest, setLogRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const logProp = { open, setOpen, logRequest, setLogRequest };

  const [qtyLogRequest, setQtyLogRequest] = useState(null);
  const [qtyOpen, setQtyOpen] = useState(false);
  const qtyLogProp = {
    open: qtyOpen,
    setOpen: setQtyOpen,
    logRequest: qtyLogRequest,
    setLogRequest: setQtyLogRequest,
  };

  const columns = [
    {
      header: "Receiving Code",
      accessorKey: "recvCode",
      cell: ({ row }) => {
        const { recvCode, customerCode } = row.original;
        return (
          <span
            style={{ color: "#1A73E8", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              setOpen(true);
              setLogRequest({ recvCode, customerCode });
            }}
          >
            {recvCode}
          </span>
        );
      },
    },
    { header: "Receiving Date", accessorKey: "dateReceived" },
    { header: "CO Number", accessorKey: "collectionOrderNo" },
    { header: "Warehouse Code", accessorKey: "warehouseCode" },
    { header: "Vendor Code", accessorKey: "vendorCode" },
    {
      header: "Split Qty",
      accessorKey: "splitQty",
      width: "150px",
      cell: ({ row }) => {
        const { recvCode, splitQty, canPalletize } = row.original;
        if (splitQty) {
          const onClick = () => {
            setQtyOpen(true);
            setQtyLogRequest({ recvCode,canPalletize });
          };
          if (!canPalletize) {
            return <Badge color="warning" content="Scanning" onClick={onClick} />;
          } else {
            return <Badge color="success" content="Complete" onClick={onClick} />;
          }
        }
        return <div></div>;
      },
    },
    { header: "VDONo", accessorKey: "vdoNo", width: "150px" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { recvCode, customerCode } = row.original;
        if (status === "Open" && queueStatus !== "NEW") {
          const OpenComponent = (
            <>
              <EditButton
                title="edit co receiving"
                disabled={!editPermission}
                onClick={() => onEdit({ recvCode, customerCode })}
              />
              <CancelButton
                title="cancel co receiving"
                disabled={!deactivatePermission}
                onClick={() => onDelete({ recvCode, customerCode })}
              />
              <MDButton
                sx={{ ml: "10px" }}
                variant="gradient"
                color="info"
                size="small"
                disabled={click}
                onClick={() => {
                  setClick(true)
                  palletizeMutation.mutate({ recvCode, customerCode },{
                    onSettled:() => setClick(false) 
                  })
                }}
              >
                {queueStatus === "" ? "palletize" : "resend palletize"}
              </MDButton>
            </>
          );
          return OpenComponent;
        } else if (status === "Palletize") {
          const PalletizeComponent = (
            <>
              <MDButton sx={{ ml: "10px" }} variant="gradient" color="error" size="small">
                cancel palletize
              </MDButton>
            </>
          );
          return PalletizeComponent;
        } else {
          return <></>;
        }
      },
    },
  ];
  const rows = data;
  return (
    <ListLayout>
      <CORecvLog {...logProp} />
      <RecvQtyLog {...qtyLogProp} />
      {palletizeStatus && <LoadingDialog/>}

      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
        {status === "Open" && queueTabs.length > 0 && (
          <BasicQueueTabs
            queueTabs={queueTabs}
            queueStatus={queueStatus}
            setQueueStatus={setQueueStatus}
          />
        )}
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" margin="10px">
          <MDButton
            variant="gradient"
            color="info"
            disabled={!addPermission}
            onClick={() => navigate("/inbound/co-receiving/add")}
          >
            <Icon>add</Icon>&nbsp;co receiving
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={rows} />
      </TableCard>
    </ListLayout>
  );
}

const Badge = ({ color, content, onClick }) => {
  return (
    <MDBadge
      sx={{ cursor: "pointer" }}
      onClick={onClick}
      variant="gradient"
      color={color}
      size="xs"
      badgeContent={content}
      container
    />
  );
};

export default CORecvList;
